import React from 'react';
import T from '@src/assets/icons/T.svg';
import H from '@src/assets/icons/H.svg';
import D from '@src/assets/icons/D.svg';
import { useSelector } from 'react-redux';
import { Area, CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { ArcSkeleton, ArcStack, ArcText } from '@src/core/ui';
import { selectTerrainMode, selectWaypoints, useGetTerrainAwareMutation } from '@src/features/order/pages/upsert-mission-page/redux';
import { TerrainMode } from '@src/features/order/pages/upsert-mission-page/redux/extra-store/types';
import { TerrainAwareProps } from './types';
import { terrainCacheKey } from '@src/features/order/pages/upsert-mission-page/config/cache-keys';
import { analyzeIntervals, calculateDistance } from '@src/features/order/pages/upsert-mission-page/utils';
import { arcConvertUnit, arcSpatium, arcSpatiumUnit } from '@src/core/extensions';
import { SETTING_UNITS_KEY } from '@src/core/config';
import { UnitType } from '@src/core/enums';
import { MeasurementType } from '@src/core/features/settings/entities';


const CRASH_ZONE_BUFFER = 30;

const safeColor = '#7B68EE';
const unsafeColor = '#FB5454';
const terrainFill = '#007200';
const crashZoneColor = '#FFC107';

const chartMargin = { right: 20, left: 10, bottom: 30 };
const whiteAxis = { stroke: 'white' };
const yAxisTick = { fill: 'white', fontSize: 11, fontFamily: "'Roboto', sans-serif" };
const xDomain: [string, string] = ['dataMin', 'dataMax'];
const yDomain: [string, string] = ['auto', 'auto'];
const containerSx = { opacity: 0.8 };


const TerrainAware: React.FC<TerrainAwareProps> = () => {
  const wps = useSelector(selectWaypoints);
  const terrainMode = useSelector(selectTerrainMode);
  const isAdvanced = terrainMode === TerrainMode.ADVANCED;
  const isAboveTerrain = terrainMode === TerrainMode.ABOVE_TERRAIN;

  const [getTerrainAware, { data = [], isLoading }] = useGetTerrainAwareMutation({
    fixedCacheKey: terrainCacheKey,
  });

  const unitType = (localStorage.getItem(SETTING_UNITS_KEY) ?? UnitType.SI) as UnitType;
  const buffer = arcConvertUnit(CRASH_ZONE_BUFFER, MeasurementType.DISTANCE, unitType);
  const unit = arcSpatiumUnit();

  const coordsKey = React.useMemo(() => {
    return JSON.stringify(wps.map((wp) => `${wp.latitude}-${wp.longitude}`));
  }, [wps]);

  const minTerrainAlt = React.useMemo(() => {
    if (data.length === 0) return 0;
    return Math.min(...data.map((d) => Number(d.altitude)));
  }, [data]);

  const wpDistances = React.useMemo(() => calculateDistance(wps), [wps]);

  const droneData = React.useMemo(() => {
    if (data.length === 0) return calculateDistance(wps);

    if (isAboveTerrain) {
      const terrainWithDist = calculateDistance(data);
      const wpDist = wpDistances;
      if (wpDist.length < 2) return calculateDistance(wps);

      const totalDroneDist = wpDist[wpDist.length - 1].distance;

      return terrainWithDist.map((point) => {
        const d = point.distance;
        let agl = Number(wpDist[0].altitude);

        for (let i = 0; i < wpDist.length - 1; i++) {
          const segStart = wpDist[i].distance;
          const segEnd = wpDist[i + 1].distance;
          if (d >= segStart && d <= segEnd) {
            const segLen = segEnd - segStart;
            const ratio = segLen === 0 ? 0 : (d - segStart) / segLen;
            agl = Number(wpDist[i].altitude) + (Number(wpDist[i + 1].altitude) - Number(wpDist[i].altitude)) * ratio;
            break;
          }
        }

        if (d > totalDroneDist) {
          agl = Number(wpDist[wpDist.length - 1].altitude);
        }

        const relativeTerrainAlt = Number(point.altitude) - minTerrainAlt;
        return { ...point, altitude: relativeTerrainAlt + agl };
      });
    }

    if (isAdvanced) return calculateDistance(wps);
    const launchAlt = Number(data[0]?.altitude);
    return calculateDistance(wps, -(launchAlt - minTerrainAlt));
  }, [wps, isAdvanced, isAboveTerrain, data, minTerrainAlt, wpDistances]);

  const terrainData = React.useMemo(() => {
    if (isAdvanced) return calculateDistance(data);
    return calculateDistance(data, minTerrainAlt);
  }, [data, isAdvanced, minTerrainAlt]);

  React.useEffect(() => {
    if (wps.length < 1) return;
    getTerrainAware(wps);
  }, [coordsKey]);

  const totalDistance = React.useMemo(() => {
    if (droneData.length === 0) return 0;
    return droneData[droneData.length - 1].distance;
  }, [droneData]);

  const xAxisTicks = React.useMemo(() => {
    if (isAboveTerrain) return wpDistances.map((d) => d.distance);
    return droneData.map((d) => d.distance);
  }, [droneData, isAboveTerrain, wpDistances]);

  const crashZoneData = React.useMemo(() => {
    return terrainData.map((point) => ({
      ...point,
      altitude: Number(point.altitude) + buffer,
    }));
  }, [terrainData, buffer]);

  const [interval] = React.useMemo(
    () => analyzeIntervals(droneData, terrainData, buffer),
    [droneData, terrainData, buffer]
  );

  const droneStroke = !interval ? safeColor : unsafeColor;

  const tickData = isAboveTerrain ? wpDistances : droneData;

  const axisTick = React.useMemo(
    () => <CustomizedAxisTick data={tickData} />,
    [tickData]
  );

  if (isLoading) return <ArcSkeleton width='100%' height='100%' />;
    
  return (
    <ArcStack 
      p={1}
      width='100%' 
      height='100%' 
      bgcolor='black'
      borderRadius={1}
      sx={containerSx}>
      <ArcText.caption 
        mb={1}
        color='white' 
        textAlign='center'
        fontFamily='Roboto'
        fontSize={12}> 
        Total Distance: {arcSpatium(totalDistance)}
      </ArcText.caption>

      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart margin={chartMargin}>
          <defs>
            <linearGradient id="terrainGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={terrainFill} stopOpacity={0.8} />
              <stop offset="100%" stopColor={terrainFill} stopOpacity={0.3} />
            </linearGradient>
            <linearGradient id="crashZoneGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={crashZoneColor} stopOpacity={0.4} />
              <stop offset="100%" stopColor={crashZoneColor} stopOpacity={0.1} />
            </linearGradient>
          </defs>

          <CartesianGrid 
            stroke="white"   
            opacity={0.2}      
            vertical={true}        
            horizontal={true}   
          />

          <XAxis
            ticks={xAxisTicks}
            interval={0}
            type='number'
            dataKey='distance'
            domain={xDomain}
            axisLine={whiteAxis}
            tickLine={whiteAxis}
            tick={axisTick} 
          />

          <YAxis 
            domain={yDomain}
            axisLine={whiteAxis} 
            tickLine={whiteAxis}
            tick={yAxisTick}
            label={{
              value: isAdvanced ? `Height (AMSL) ${unit}` : `Height (Above Terrain) ${unit}`,
              angle: -90,
              position: 'insideLeft',
              fill: 'white',
              fontSize: 11,
              style: { textAnchor: 'middle', fontFamily: "'Roboto', sans-serif" },
            }}
          />

          <Area
            type="monotone"
            data={crashZoneData}
            dataKey="altitude"
            stroke={crashZoneColor}
            strokeDasharray="5 5"
            strokeWidth={1.5}
            fill="url(#crashZoneGradient)"
            isAnimationActive={false}
            dot={false}
            activeDot={false}
          />

          <Area
            type="monotone"
            data={terrainData}
            dataKey="altitude"
            stroke={terrainFill}
            strokeWidth={1}
            fill="url(#terrainGradient)"
            isAnimationActive={false}
            dot={false}
            activeDot={false}
          />

          <Line 
            type="linear"
            data={droneData}
            dataKey="altitude"
            stroke={droneStroke}
            strokeWidth={2}
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />

        </ComposedChart>
      </ResponsiveContainer>

      <ChartLegend />
    </ArcStack>

  );
};


const ChartLegend: React.FC = () => (
  <ArcStack direction="row" justifyContent="flex-start" gap={2} mt={0.5} pl={5}>
    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack 
        width={28} 
        height={6} 
        sx={{ bgcolor: terrainFill, borderRadius: 1 }}
      >
        <ArcStack 
          width={12} 
          height={4} 
          sx={{ 
            bgcolor: '#00720040', 
            border: '0.5px solid', 
            borderColor: terrainFill, 
            borderRadius: 1 }}
        />
      </ArcStack>
      <ArcText.caption color="white" fontSize={10}>Terrain</ArcText.caption>
    </ArcStack>

    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack 
        width={28} 
        height={6} 
        sx={{ 
          border: '1.5px dashed', 
          borderColor: crashZoneColor, 
          borderRadius: 3,
          bgcolor: `${crashZoneColor}20`,
        }} 
      />
      <ArcText.caption color="white" fontSize={10}>Crash zone</ArcText.caption>
    </ArcStack>

    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack 
        width={28} 
        height={6} 
        sx={{ bgcolor: unsafeColor, borderRadius: 3 }} 
      />
      <ArcText.caption color="white" fontSize={10}>Unsafe drone path</ArcText.caption>
    </ArcStack>

    <ArcStack direction="row" alignItems="center" gap={0.5}>
      <ArcStack 
        width={28} 
        height={6} 
        sx={{ bgcolor: safeColor, borderRadius: 3 }} 
      />
      <ArcText.caption color="white" fontSize={10}>Safe drone path</ArcText.caption>
    </ArcStack>
  </ArcStack>
);


const CustomizedAxisTick = (props: any) => {
  const { x, y, payload, data } = props;

  const index = payload.index;
  const current = data[index];

  if (index === 0) {
    return (
      <g transform={`translate(${x},${y})`}>
        <image href={T} x={-8} y={-25} width={16} height={16} />
        <TickText> {payload.value} </TickText>
      </g>
    );
  }

  if (index === data.length - 1) {
    return (
      <g transform={`translate(${x},${y})`}>
        <image href={H} x={-8} y={-25} width={16} height={16} />
        <TickText> {payload.value} </TickText>
      </g>
    );
  }

  if (current?.orderId) {
    return (
      <g transform={`translate(${x},${y})`}>
        <image href={D} x={-8} y={-25} width={16} height={16} />
        <TickText> {payload.value} </TickText>
      </g>
    );
  }

  return (
    <g transform={`translate(${x},${y})`}>
      <g transform="translate(0, -16)">
        <circle r={8} fill='#9664F8' />
        <text
          fill="white"
          fontSize={10}
          fontWeight="bold"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {current.wpCount}
        </text>
      </g>

      <TickText> {payload.value} </TickText>
    </g>
  );
};


const TickText: React.FC<React.PropsWithChildren> = ({ children }) => (
  <text 
    x={0} 
    y={0} 
    dy={4} 
    fill="white" 
    fontSize={11}
    fontFamily="'Roboto', sans-serif"
    textAnchor='end' 
    transform="rotate(-90)"
  >
    {children} 
  </text>
);


export default TerrainAware;
